import React, { useEffect, useState } from "react";
import { Volume2, VolumeX, BellRing, Bell, BellOff, Monitor, MonitorOff, CheckCircle2, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

import { usePushNotifications } from "../../hooks/usePushNotifications";

/**
 * CanaisStatus — card de prontidão de canais de notificação do empreendedor.
 *
 * Mostra 3 sinais críticos para garantir que o lojista receberá novos pedidos:
 *  1. Som — habilitado (localStorage `dlv-sound`)
 *  2. Push — permission granted + isSubscribed (usePushNotifications)
 *  3. Browser ativo — aba focada (document.hidden + visibilitychange)
 *
 * Se todos os 3 ✅ → barra verde "Pronto para receber pedidos"
 * Se algum ⚠️ → barra amarela com link para o Kanban (onde os controles ficam)
 */
export default function CanaisStatus({ negocioId }) {
  const { isSupported, permission, isSubscribed } = usePushNotifications();
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try { return localStorage.getItem("dlv-sound") !== "off"; }
    catch { return true; }
  });
  const [browserActive, setBrowserActive] = useState(
    typeof document === "undefined" ? true : !document.hidden
  );

  // Reage a mudanças de visibilidade
  useEffect(() => {
    const handler = () => setBrowserActive(!document.hidden);
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, []);

  // Reage a mudanças no localStorage (outras abas)
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "dlv-sound") {
        setSoundEnabled(e.newValue !== "off");
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const pushReady = isSupported && permission === "granted" && isSubscribed;
  const pushBlocked = isSupported && permission === "denied";
  const allReady = soundEnabled && pushReady && browserActive;

  const wrapperCls = allReady
    ? "bg-emerald-50 border-emerald-200"
    : "bg-amber-50 border-amber-200";

  return (
    <section
      className={`rounded-2xl border p-4 ${wrapperCls}`}
      data-testid="canais-status-card"
      data-state={allReady ? "ready" : "incomplete"}
    >
      <div className="flex items-center gap-2 mb-3">
        {allReady ? (
          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
        ) : (
          <AlertTriangle className="w-5 h-5 text-amber-600" />
        )}
        <h2 className="text-sm font-semibold text-[var(--text-primary)]">
          {allReady
            ? "Pronto para receber pedidos"
            : "Atenção: alguns canais precisam de ajustes"}
        </h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <CanalItem
          icon={soundEnabled ? Volume2 : VolumeX}
          label="Som"
          status={soundEnabled ? "ok" : "off"}
          okText="Bipe de novo pedido habilitado"
          offText="Silenciado — clique no botão de som no Kanban"
          testid="canal-som"
        />
        <CanalItem
          icon={pushReady ? BellRing : pushBlocked ? BellOff : Bell}
          label="Push"
          status={pushReady ? "ok" : pushBlocked ? "blocked" : "off"}
          okText="Notificação chega mesmo com aba fechada"
          offText="Ative no Kanban (botão 'Ativar avisos')"
          blockedText="Bloqueado — desbloqueie nas configurações do navegador"
          testid="canal-push"
        />
        <CanalItem
          icon={browserActive ? Monitor : MonitorOff}
          label="Aba ativa"
          status={browserActive ? "ok" : "off"}
          okText="Esta aba está em foco"
          offText="Aba em segundo plano — push entra em ação"
          testid="canal-browser"
        />
      </div>

      {!allReady && negocioId && (
        <Link
          to={`/empreendedor/delivery/${negocioId}`}
          className="inline-flex items-center gap-1.5 mt-3 text-xs text-amber-800 font-semibold underline hover:text-amber-900"
          data-testid="canais-status-fix"
        >
          Ajustar agora no Kanban →
        </Link>
      )}
    </section>
  );
}

function CanalItem({ icon: Icon, label, status, okText, offText, blockedText, testid }) {
  const palette = status === "ok"
    ? { bg: "bg-white", iconCls: "text-emerald-600", text: okText }
    : status === "blocked"
    ? { bg: "bg-white", iconCls: "text-red-600", text: blockedText || offText }
    : { bg: "bg-white", iconCls: "text-amber-600", text: offText };
  return (
    <div
      className={`flex items-start gap-2 p-2.5 rounded-lg ${palette.bg} border border-[var(--border)]`}
      data-testid={testid}
      data-status={status}
    >
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${palette.iconCls}`} />
      <div className="min-w-0">
        <p className="text-xs font-semibold text-[var(--text-primary)]">{label}</p>
        <p className="text-[11px] text-[var(--text-secondary)] leading-snug">
          {palette.text}
        </p>
      </div>
    </div>
  );
}
